// Imports
import React, { useState } from "react";
import { message } from "antd";
import CreateContract from "../CreateContract/CreateContract";
import axios from "../../axios/AxiosInstance";
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay, useDroppable, pointerWithin } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Copy, Edit, X, Check, Clock, Users, CheckCircle, Send, XCircle, Mail, Phone, MapPin, Calendar, Briefcase, Euro, Hash, Building, MoreVertical, FileText } from "lucide-react";
import { Button } from "../../components/reusableComponents/Button";

// InfoItem
const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 mt-1 text-indigo-300 flex-shrink-0" />
    <div className="flex flex-col">
      <span className="text-xs text-gray-400">{label}</span>
      <span className="text-sm font-medium text-gray-800">{value}</span>
    </div>
  </div>
);

// Draggable card
const DraggableCandidateCard = ({ candidate }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: String(candidate._id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="group flex items-center justify-between p-2.5 rounded-lg transition-all duration-200 hover:bg-white/10 cursor-move"
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 uppercase rounded-full bg-black p-1 flex items-center justify-center font-bold text-sm text-white border-2 ">
          {candidate.firstname?.[0]}
          {candidate.lastname?.[0]}
        </div>
        <span className="text-sm capitalize font-medium text-black group-hover:text-gray-700">
          {candidate.firstname} {candidate.lastname}
        </span>
      </div>
    </div>
  );
};

const STATUSES = [
  "candidates",
  "contactedCandidates",
  "acceptedCandidates",
  "refusedByAgence",
  "submittedToClient",
  "confirmedCandidates",
];

const STATUSES_TITLES = [
  "candidat(s) disponible(s)",
  "candidat(s) contacté(s)",
  "candidat(s) accepté(s)",
  "refusé(s) par l'agence",
  "soumis au client",
  "candidat(s) confirmé(s)",
];

const CandidateColumn = ({ title, status, candidates, showCv }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${status}` });

  const textColor = isOver ? "text-indigo-600" : "text-gray-800";
  const borderColor = isOver ? "border-indigo-400" : "border-gray-200";
  const bgColor = isOver ? "bg-indigo-50" : "bg-white";

  return (
    <div
      ref={setNodeRef}
      className={`transition-colors duration-200 ${bgColor} border ${borderColor} rounded-xl flex flex-col h-full`}
    >
      <div className="p-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
        <h4 className={`text-sm font-semibold text-center ${textColor}`}>
          {title}
        </h4>
      </div>
      <div className="p-3 space-y-3 overflow-y-auto flex-1 min-h-[150px]">
        <SortableContext
          items={(candidates || []).map((c) => String(c._id))}
          strategy={verticalListSortingStrategy}
        >
          {candidates?.length > 0 ? (
            candidates.map((c) => (
              <div
                className="flex group items-center justify-between"
                key={`${status}-${c._id}`}
              >
                <DraggableCandidateCard candidate={c} />
                <div className="flex items-center gap-1 text-black opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => showCv(e, c)}
                    className="p-1 hover:bg-white/20 rounded"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <button className="p-1 hover:bg-white/20 rounded">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-xs text-gray-400 pt-4">
              Aucun candidat
            </p>
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const StatusStep = ({
  icon: Icon,
  title,
  count,
  isActive,
  isFirst,
  isLast,
}) => (
  <div className="relative flex-1 flex flex-col pb-3 items-center text-center">
    {!isFirst && (
      <div
        className={`absolute top-1/2 left-0 w-1/2 h-0.5 -translate-y-1/2 ${isActive ? "bg-indigo-500" : "bg-gray-300"}`}
      />
    )}
    {!isLast && (
      <div
        className={`absolute top-1/2 right-0 w-1/2 h-0.5 -translate-y-1/2 ${isActive ? "bg-indigo-500" : "bg-gray-300"}`}
      />
    )}
    <div
      className={`relative z-10 w-12 h-12 flex items-center justify-center rounded-full border-2 ${isActive ? "bg-indigo-500 text-white border-indigo-400 shadow-md" : "bg-white text-gray-400 border-gray-300"}`}
    >
      <Icon className="w-6 h-6" />
    </div>
    <p
      className={`mt-3 text-xs font-semibold ${isActive ? "text-gray-800" : "text-gray-400"}`}
    >
      {title}
    </p>
    <p className={`text-xs ${isActive ? "text-indigo-500" : "text-gray-400"}`}>
      {count} candidats
    </p>
  </div>
);

export const OrderDetails = ({ commande: initialCommande, onClose }) => {
  const [commande, setCommande] = useState(initialCommande);
  const [activeCandidate, setActiveCandidate] = useState(null);
  const [contractRequest, setContractRequest] = useState(null);
  const [preparingContract, setPreparingContract] = useState(false);
  const prepareContract = async candidate => {
    setPreparingContract(true);
    try {
      const id = commande.client?._id || commande.client;
      if (!id) throw new Error("Client missing");
      const { data: client } = await axios.get(`client/${id}`);
      const type = String(commande.typeContrat || commande.contractType || "Intérim").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const time = value => { const match = String(value || "").match(/^(\d{2}):?(\d{2})/); return match ? new Date(2000,0,1,Number(match[1]),Number(match[2])) : null; };
      setContractRequest({client:{...client, contacts:client.contacts || []}, initial:{candidate,contractType:type,contact:commande.contact || "",startDate:commande.startDate ? new Date(commande.startDate) : null,endDate:commande.endDate ? new Date(commande.endDate) : null,startHour:time(commande.startHour),endHour:time(commande.endHour),motif:commande.motif || "",justificatif:commande.justificatif || "",nbHeureHebdo:commande.nbHeureHebdo || "",task:commande.task || "",orderId:commande._id}});
    } catch { message.error("Impossible de préparer le contrat. Vérifiez le client de la commande."); }
    finally { setPreparingContract(false); }
  };
  const requestInvoice = candidate => {
    const subject = `Demande de facturation — ${commande.client?.companyName || commande.companyName || commande.clientName || "Commande"}`;
    const body = ["Bonjour,", "", "Merci de préparer la facturation de cette commande après confirmation du candidat.", `Commande : ${commande.orderNumber || commande._id}`, `Client : ${commande.client?.companyName || commande.companyName || commande.clientName || "—"}`, `Candidat confirmé : ${candidate.firstname || ""} ${candidate.lastname || ""}`, `Type : ${commande.typeContrat || commande.contractType || "—"}`, `Poste : ${commande.qualification || "—"}`, `Période : ${commande.startDate || "—"} au ${commande.endDate || "—"}`, `Horaires : ${commande.startHour || "—"} à ${commande.endHour || "—"}`, `Taux horaire renseigné : ${commande.hourlyRate || "—"}`, `Freelance : ${localStorage.getItem("firstname") || ""} ${localStorage.getItem("lastname") || ""}`, "", "Merci de confirmer la prise en charge."].join("\n");
    window.location.href = `mailto:contact@jm-interim.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // ✅ États pour le CV
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [cvData, setCvData] = useState(null);
  const [cvType, setCvType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalCV, setIsModalCV] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const getStatusBadge = () => {
    const isComplete =
      commande.confirmedCandidates?.length >= commande.quantity ||
      commande.status === "terminee";
    const status = isComplete ? "Complète" : "Incomplète";
    const color = isComplete
      ? "bg-green-100 text-green-700"
      : "bg-yellow-100 text-yellow-700";
    return (
      <span
        className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${color}`}
      >
        {status}
      </span>
    );
  };

  const handleDragStart = (event) => {
    const candidate = STATUSES.flatMap((status) => commande[status] || []).find(
      (c) => String(c._id) === String(event.active.id),
    );
    setActiveCandidate(candidate);
  };

  const handleDragOver = () => {};

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    let sourceStatus = null;
    let targetStatus = null;

    for (let status of STATUSES) {
      if ((commande[status] || []).find((c) => String(c._id) === activeId)) {
        sourceStatus = status;
      }
      if ((commande[status] || []).find((c) => String(c._id) === overId)) {
        targetStatus = status;
      }
    }

    if (overId.startsWith("column-")) {
      const columnStatus = overId.replace("column-", "");
      if (STATUSES.includes(columnStatus)) {
        targetStatus = columnStatus;
      }
    }

    if (!sourceStatus || !targetStatus) return;

    const candidate = commande[sourceStatus].find(
      (c) => String(c._id) === activeId,
    );
    if (!candidate) return;

    setCommande((prev) => {
      const updated = { ...prev };
      updated[sourceStatus] = prev[sourceStatus].filter(
        (c) => String(c._id) !== activeId,
      );

      const targetItems = [...(prev[targetStatus] || [])];
      const overIndex = targetItems.findIndex((c) => String(c._id) === overId);

      if (overId.startsWith("column-") || overIndex === -1) {
        targetItems.unshift(candidate);
      } else {
        targetItems.splice(overIndex, 0, candidate);
      }

      updated[targetStatus] = targetItems;
      console.log(updated);
      return updated;
    });

    setActiveCandidate(null);
  };

  const statusSteps = [
    {
      title: "Disponibles",
      icon: Users,
      count: commande.candidates?.length || 0,
    },
    {
      title: "Contactés",
      icon: Clock,
      count: commande.contactedCandidates?.length || 0,
    },
    {
      title: "Accepté",
      icon: CheckCircle,
      count: commande.acceptedCandidates?.length || 0,
    },
    {
      title: "Proposé",
      icon: Send,
      count: commande.submittedToClient?.length || 0,
    },
    {
      title: "Confirmé",
      icon: Check,
      count: commande.confirmedCandidates?.length || 0,
    },
  ];

  // ✅ Fonction showCv
  const showCv = (e, candidate) => {
    e.stopPropagation();
    const candidateId = candidate._id;
    setLoadingSearch(true);
    axios
      .get(`cv/${candidateId}`)
      .then((res) => {
        const base64 = res.data.data;
        setCvData(base64);
        const header = base64.substring(0, 5);

        if (header === "JVBER") {
          setCvType("pdf");
        } else if (header.startsWith("/9j/")) {
          setCvType("jpeg");
        } else if (header.startsWith("iVBOR")) {
          setCvType("png");
        } else {
          console.log("Unsupported file type");
        }

        setSelectedUser(candidate);
        setIsModalCV(true);
      })
      .catch((e) => console.log(e))
      .finally(() => setLoadingSearch(false));
  };

  const activeStepIndex = statusSteps
    .slice()
    .reverse()
    .findIndex((step) => step.count > 0);
  const currentStep =
    activeStepIndex !== -1 ? statusSteps.length - 1 - activeStepIndex : -1;

  return (
    <div className="w-full mx-auto p-8 bg-white rounded-3xl border border-gray-200 shadow-xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-6 mb-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            {commande.typeContrat} {getStatusBadge()}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Recruteur :{" "}
            <span className="font-medium text-gray-700">
              {commande.recruiter?.firstname || "-"}
            </span>
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          className="text-gray-500 hover:text-black"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Status steps */}
      <div className="flex justify-between items-start gap-4 mb-10">
        {statusSteps.map((step, i) => (
          <StatusStep
            key={i}
            {...step}
            isActive={i <= currentStep}
            isFirst={i === 0}
            isLast={i === statusSteps.length - 1}
          />
        ))}
      </div>

      {/* Infos */}
      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">
          Informations de la commande
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
          <div className="flex flex-col gap-4">
            <InfoItem
              icon={Building}
              label="Client"
              value={commande.client?.companyName}
            />
            <InfoItem
              icon={Users}
              label="Contact"
              value={commande.contactData?.name}
            />
            <InfoItem
              icon={Phone}
              label="Téléphone"
              value={commande.contactData?.phone}
            />
            <InfoItem
              icon={Mail}
              label="Email"
              value={commande.contactData?.email}
            />
          </div>
          <div className="flex flex-col gap-4">
            <InfoItem
              icon={Briefcase}
              label="Type de contrat"
              value={commande.typeContrat}
            />
            <InfoItem
              icon={Calendar}
              label="Date de début"
              value={`${new Date(commande.startDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}`}
            />
            <InfoItem
              icon={Clock}
              label="Horaires"
              value={`${commande.startHour} - ${commande.endHour}`}
            />
            <InfoItem
              icon={Calendar}
              label="Jours travaillés"
              value={(commande.workedDays || []).join(", ")}
            />
          </div>
          <div className="flex flex-col gap-4">
            <InfoItem icon={MapPin} label="Lieu" value={commande.town} />
            <InfoItem
              icon={Briefcase}
              label="Poste"
              value={commande.qualification}
            />
            <InfoItem icon={Hash} label="Quantité" value={commande.quantity} />
            <InfoItem icon={Users} label="EPI" value={commande.epi} />
          </div>
          <div className="flex flex-col gap-4">
            <InfoItem
              icon={Euro}
              label="Taux horaire"
              value={commande.hourlyRate}
            />
            <InfoItem icon={Euro} label="Primes" value={commande.bonus} />
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 mt-1 text-indigo-300 flex-shrink-0" />
              <div className="flex flex-col">
                <span className="text-xs text-gray-400">Tâches</span>
                <p className="text-sm font-medium text-gray-800 whitespace-pre-wrap">
                  {commande.task}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(commande.confirmedCandidates || []).length > 0 && <section className="my-6 rounded-xl border border-blue-200 bg-blue-50 p-5">
        <h2 className="text-lg font-semibold text-slate-900">Candidats confirmés : préparer les documents</h2>
        <p className="mt-1 text-sm text-slate-600">Préparez le contrat ou demandez la facturation à l’agence. La demande de facture ouvre un email à envoyer.</p>
        <div className="mt-4 space-y-3">{commande.confirmedCandidates.map(candidate => <div key={candidate._id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white p-3"><strong className="text-sm text-slate-800">{candidate.firstname} {candidate.lastname}</strong><div className="flex flex-wrap gap-2"><button disabled={preparingContract} onClick={() => prepareContract(candidate)} className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Préparer le contrat</button><button onClick={() => requestInvoice(candidate)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-blue-700">Demander la facture</button></div></div>)}</div>
      </section>}
      {contractRequest && <CreateContract client={contractRequest.client} initialContract={contractRequest.initial} qualifications={[]} chantiers={[]} isCreateContract setIsCreateContract={value => { if (!value) setContractRequest(null); }} />}

      {commande.dateRanges?.length > 1 && <section className="my-5 rounded-xl border border-slate-200 bg-white p-4"><h2 className="mb-3 font-semibold text-slate-900">Dates de la commande</h2><div className="flex flex-wrap gap-2">{commande.dateRanges.map((period,index) => <span key={index} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">{period.startDate}{period.endDate !== period.startDate && ` → ${period.endDate}`}{period.startHour && ` · ${period.startHour}–${period.endHour}`}</span>)}</div></section>}
      {/* Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="my-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 min-h-[300px]">
          {STATUSES.map((status, idx) => (
            <CandidateColumn
              key={status}
              title={STATUSES_TITLES[idx]}
              status={status}
              candidates={commande[status]}
              showCv={showCv}
            />
          ))}
        </div>
        <DragOverlay>
          {activeCandidate ? (
            <DraggableCandidateCard candidate={activeCandidate} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer actions */}
      <div className="flex justify-end gap-3 mt-8 flex-wrap">
        <Button variant="outline" size="sm">
          <Copy className="w-4 h-4 mr-2" /> Dupliquer
        </Button>
        <Button variant="outline" size="sm">
          <Edit className="w-4 h-4 mr-2" /> Modifier
        </Button>
        <Button variant="destructive" size="sm">
          <XCircle className="w-4 h-4 mr-2" /> Annuler
        </Button>
        <Button
          size="sm"
          className="bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <Check className="w-4 h-4 mr-2" /> Terminer
        </Button>
      </div>

      {/* ✅ Modal de visualisation du CV */}
      {isModalCV && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white w-[1000px] h-[800px] p-6 rounded-xl  pb-20">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">
                CV de {selectedUser?.firstname} {selectedUser?.lastname}
              </h2>
              <button
                onClick={() => setIsModalCV(false)}
                className="text-gray-600 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {loadingSearch ? (
              <p className="text-center">Chargement...</p>
            ) : cvType === "pdf" ? (
              <iframe
                title="CV"
                src={`data:application/pdf;base64,${cvData}`}
                className="w-full h-full"
              />
            ) : cvType === "jpeg" || cvType === "png" ? (
              <img
                src={`data:image/${cvType};base64,${cvData}`}
                alt="CV"
                className="w-full"
              />
            ) : (
              <p className="text-center text-red-500">
                Format de CV non supporté
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
